import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" },
  }),
};

export default function HeroSection({ data, registerSection }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) registerSection("hero", ref.current);
  }, [registerSection]);

  if (!data) return null;

  const handleScrollTo = (e, target) => {
    e.preventDefault();
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="hero"
      className="relative overflow-hidden hero-gradient noise-overlay"
    >
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: Copy */}
          <div className="lg:col-span-7">
            <motion.p
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              {data.eyebrow}
            </motion.p>

            <motion.h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] text-foreground"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              {data.headline}
            </motion.h1>

            <motion.p
              className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              {data.subheadline}
            </motion.p>

            <motion.div
              className="mt-8 flex flex-wrap gap-3"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              <Button
                data-testid="hero-primary-cta-button"
                className="h-12 px-6 text-sm rounded-xl bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 btn-primary-hover"
                onClick={(e) => handleScrollTo(e, "#pricing")}
              >
                {data.primary_cta}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-secondary-cta-button"
                variant="outline"
                className="h-12 px-6 text-sm rounded-xl border-border hover:border-[hsl(var(--ring))] hover:bg-accent"
                onClick={(e) => handleScrollTo(e, "#solutions")}
              >
                {data.secondary_cta}
              </Button>
            </motion.div>

            {/* Trust Metrics */}
            <motion.div
              className="mt-12 flex flex-wrap gap-8"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
            >
              {data.trust_metrics?.map((metric, i) => (
                <div key={i} data-testid="hero-trust-metric">
                  <p className="font-heading text-2xl md:text-3xl text-foreground">
                    {metric.value}
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">
                    {metric.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Intake Process Card */}
          <motion.div
            className="lg:col-span-5"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
          >
            <Card className="rounded-2xl bg-card border border-border shadow-[var(--shadow-soft)] p-6 md:p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-6">
                Private Intake Process
              </p>
              <div className="space-y-5">
                {data.intake_steps?.map((step, i) => (
                  <div
                    key={step.step}
                    className="flex items-start gap-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
                      <span className="font-heading text-lg text-foreground">
                        {step.step}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {step.title}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {step.description}
                      </p>
                    </div>
                    {i < data.intake_steps.length - 1 && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground/40 mt-3 ml-auto hidden sm:block" />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Every engagement begins with a confidential, no-obligation consultation.
                </p>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
